import { motion } from "framer-motion";
import { Eye, AudioLines, Pointer } from "lucide-react";

const FEATURES = [
  { icon: Eye, title: "See It", body: "Visual confirmation", id: "see" },
  { icon: AudioLines, title: "Hear It", body: "Audible confirmation", id: "hear" },
  { icon: Pointer, title: "Feel It", body: "Tactile confirmation", id: "feel" },
];

const reveal = {
  hidden: { opacity: 0, y: 32 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: i * 0.12 },
  }),
};

export default function Solution() {
  return (
    <section id="solution" className="bg-[#0a1f33] py-20 sm:py-28 scroll-mt-16 overflow-hidden" data-testid="solution-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <motion.p
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            custom={0}
            className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400 mb-4"
          >
            The Solution
          </motion.p>
          <motion.h2
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            custom={1}
            className="font-display font-extrabold uppercase leading-[1.05] tracking-tight text-4xl sm:text-5xl text-white"
          >
            Confidence
            <br />
            In Every Connection.
          </motion.h2>
          <motion.span variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1} className="block w-12 h-1 bg-[#1c9ce8] mt-6" />
          <motion.p
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            custom={2}
            className="mt-7 text-slate-300 leading-relaxed max-w-md"
            data-testid="solution-body"
          >
            Verix H₂O is LeakLock's next-generation push-fit connection system,
            designed to give the installer clear confirmation when the connection
            is secure.
          </motion.p>

          <div className="mt-12 flex gap-8 sm:gap-12">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.id}
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                custom={i + 2}
                className="group text-center"
                data-testid={`solution-feature-${f.id}`}
              >
                <span className="mx-auto flex items-center justify-center w-16 h-16 rounded-full border-2 border-[#1c9ce8]/70 group-hover:bg-[#1c9ce8]/15 group-hover:shadow-[0_0_26px_rgba(28,156,232,0.4)] transition-[background-color,box-shadow] duration-400">
                  <f.icon className="w-6 h-6 text-[#2aa7ec]" strokeWidth={1.8} />
                </span>
                <p className="mt-4 font-display font-bold uppercase tracking-wide text-sm text-white">{f.title}</p>
                <p className="text-xs text-slate-400 mt-1">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 48 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <img
            src="/fitting-hand.png"
            alt="Installer holding a VERIXX H2O fitting on pipe"
            className="w-full img-feather select-none"
            data-testid="solution-product-image"
          />
          <p className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 font-display font-extrabold uppercase leading-[1.1] text-right text-lg sm:text-2xl text-white" data-testid="solution-tagline">
            Simple.
            <br />
            Secure.
            <br />
            <span className="text-[#2aa7ec]">Smarter.</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
